import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Grid,
  Button,
  Select,
  MenuItem,
  TextField,
  Chip,
  Tabs,
  Tab,
  Snackbar,
  Alert as MuiAlert,
  Card,
  CardContent,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Checkbox,
  FormControlLabel,Grow,Slide
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const NoRowsOverlay = () => (
  <Box sx={{ p: 2, textAlign: "center" }}>No Data</Box>
);



const SummarySection = () => {
  const [startDatetime, setStartDatetime] = useState(dayjs());
  const [endDatetime, setEndDatetime] = useState(dayjs());
  const [machineGroups, setMachineGroups] = useState([]);
  const [machines, setMachines] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState("");
  const [machineData, setMachineData] = useState({});
  const [plannedJobsData, setPlannedJobsData] = useState({});
  const [tabValue, setTabValue] = useState(0);
  const [snackbars, setSnackbars] = useState([]);
  const [columnDialogOpen, setColumnDialogOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState([
    "Employee_ID",
    "name",
    "jobNo",
    "customer",
    "product",
    "mainOperation",
    "manualReason",
    "InputQty",
    "PassQty",
    "productionQty",
    "wasteQty",
    "finishedGood",
    "startDatetime",
    "endDatetime",
    "jobCloseremarks",
    "approval_status",
    "tracking_status",
  ]);
  const [checked, setChecked] = useState(false);

  const enqueueSnackbar = (type, message) => {
    const uniqueId = Date.now().toString(); // Generate unique ID for snackbar
    setSnackbars((prev) => [...prev, { id: uniqueId, type, message }]);
  };

  const handleSnackbarClose = (id) => {
    setSnackbars((prev) => prev.filter((snackbar) => snackbar.id !== id));
  };

  useEffect(() => {
    setChecked(true); // Trigger animation on component mount
  }, []);

  useEffect(() => {
    axios
      .get(`${API_BASE_URL}/MachineGroupCodeWithNames`)
      .then((response) => {
        setMachineGroups(response.data);
      })
      .catch(() => enqueueSnackbar("error", "Failed to fetch machine groups in summary section."));
  }, []);

  const handleGroupChange = (event) => {
    const group = event.target.value;
    setSelectedGroup(group);
    setMachines([]);
    setMachineData({});
    setPlannedJobsData({});
  };

  const fetchMachines = (groupCode) => {
    axios
      .get(`${API_BASE_URL}/MachineCodeWithNames?groupCode=${groupCode}`)
      .then((response) => {
        const allMachines = response.data.map((machine) => machine.MachineCode);
        setMachines(allMachines);
        fetchDataForMachines(allMachines);
      })
      .catch(() => enqueueSnackbar("error", "Failed to fetch machines."));
  };

  const fetchDataForMachines = async (machineCodes) => {
    const summaryByMachine = {};
    const planningByMachine = {};
  
    for (const machineCode of machineCodes) {
      try {
        console.log(`Fetching summary and planned jobs for machine code: ${machineCode}`);
  
        // Fetch summary data
        const summaryResponse = await axios.post(
          `${API_BASE_URL}/viewapprovedsummary`,
          {
            startDatetime: startDatetime.format("YYYY-MM-DD HH:mm:ss"),
            endDatetime: endDatetime.format("YYYY-MM-DD HH:mm:ss"),
            machineCode,
          }
        );
        summaryByMachine[machineCode] = summaryResponse.data.summary || [];
        console.log(`Summary data for ${machineCode}:`, summaryByMachine[machineCode]);
  
        // Fetch planned jobs data
        const planningResponse = await axios.post(
          `${API_BASE_URL}/fetchPlannedJobs`,
          {
            endDatetime: endDatetime.format("YYYY-MM-DD HH:mm:ss"),
            machineCode,
          }
        );
        planningByMachine[machineCode] = planningResponse.data.data || [];
        console.log(`Planned jobs data for ${machineCode}:`, planningByMachine[machineCode]);
  
      } catch (error) {
        console.error(`Error fetching data for machine code: ${machineCode}`, error);
        enqueueSnackbar("warning", `No data available for machine: ${machineCode}`);
        summaryByMachine[machineCode] = [];
        planningByMachine[machineCode] = [];
      }
    }
  
    console.log("Final summary data:", summaryByMachine);
    console.log("Final planned jobs data:", planningByMachine);
  
    setMachineData(summaryByMachine);
    setPlannedJobsData(planningByMachine);
  };
  

  const handleTrackingUpdate = async (id, machineCode) => {
    try {
      const response = await axios.put(
        `${API_BASE_URL}/updateTrackingStatus`,
        {
          id,
          tracking_status: "tracked",
        }
      );

      if (response.data.success) {
        setMachineData((prevData) => ({
          ...prevData,
          [machineCode]: prevData[machineCode].map((row) =>
            row.id === id ? { ...row, tracking_status: "tracked" } : row
          ),
        }));
        enqueueSnackbar("success", "Tracking status updated successfully!");
      } else {
        enqueueSnackbar("error", "Failed to update tracking status.");
      }
    } catch {
      enqueueSnackbar("error", "Failed to update tracking status.");
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleView = () => {
    if (!startDatetime || !endDatetime || !selectedGroup) {
      enqueueSnackbar("error", "Please select a valid time range and machine group.");
      return;
    }

    if (dayjs(endDatetime).isBefore(dayjs(startDatetime))) {
      enqueueSnackbar("error", "End datetime must be greater than start datetime.");
      return;
    }

    fetchMachines(selectedGroup);
  };

  const allColumns = [
    { field: "Employee_ID", headerName: "Employee ID", width: 150 },
    { field: "name", headerName: "Name", width: 150 },
    { field: "jobNo", headerName: "Job No", width: 150 },
    { field: "customer", headerName: "Customer", width: 200 },
    { field: "product", headerName: "Product", width: 200 },
    { field: "mainOperation", headerName: "Main Operation", width: 200 },
    { field: "manualReason", headerName: "Manual Reason", width: 150 },
    { field: "InputQty", headerName: "Input Qty", width: 120 },
    { field: "PassQty", headerName: "Pass Qty", width: 120 },
    { field: "productionQty", headerName: "Production Qty", width: 150 },
    { field: "wasteQty", headerName: "Waste Qty", width: 150 },
    { field: "finishedGood", headerName: "Finished Good", width: 150 },
    {
      field: "startDatetime",
      headerName: "Start Datetime",
      width: 200,
      renderCell: (params) => dayjs(params.value).format("YYYY-MM-DD HH:mm:ss"),
    },
    {
      field: "endDatetime",
      headerName: "End Datetime",
      width: 200,
      renderCell: (params) => dayjs(params.value).format("YYYY-MM-DD HH:mm:ss"),
    },
    { field: "jobCloseremarks", headerName: "Job Close Remarks", width: 200 },
    { field: "totalSetupTime", headerName: "Total Setup Time", width: 150 },
    { field: "totalIdleTime", headerName: "Total Idle Time", width: 150 },
    { field: "totalProductionTime", headerName: "Total Production Time", width: 200 },
    { field: "totalMealsTime", headerName: "Total Meals Time", width: 150 },
    { field: "idleReasons", headerName: "Idle Reasons", width: 200 },
    { field: "status", headerName: "Status", width: 150 },
    { field: "avg_prod_speed", headerName: "Avg Production Speed", width: 200 },
    { field: "avg_op_speed", headerName: "Avg Operation Speed", width: 200 },
    {
      field: "approval_status",
      headerName: "Approval Status",
      width: 150,
      renderCell: (params) => (
        <Chip
          variant="outlined"
          size="small"
          label={params.value}
          style={{
            backgroundColor:
              params.value === "Approved" ? "rgba(0, 128, 0, 0.2)" : "rgba(255, 0, 0, 0.2)",
            color: params.value === "Approved" ? "green" : "red",
          }}
        />
      ),
    },
    {
      field: "tracking_status",
      headerName: "Tracking Status",
      width: 200,
      renderCell: (params) => (
        <Chip
          label={params.value}
          onClick={
            params.value === "untracked"
              ? () => handleTrackingUpdate(params.row.id, params.row.machineCode)
              : undefined
          }
          style={{
            cursor: params.value === "untracked" ? "pointer" : "default",
            backgroundColor:
              params.value === "tracked" ? "rgba(25, 118, 210, 0.2)" : "rgba(0, 0, 0, 0.1)",
            color: params.value === "tracked" ? "#1976d2" : "#000",
          }}
          variant={params.value === "untracked" ? "outlined" : "filled"}
        />
      ),
    },
  ];

  const summaryColumns = allColumns.filter((col) =>
    visibleColumns.includes(col.field)
  );
  const handleColumnDialogToggle = () => {
    setColumnDialogOpen(!columnDialogOpen);
  };

  const handleColumnChange = (field) => {
    setVisibleColumns((prev) =>
      prev.includes(field) ? prev.filter((col) => col !== field) : [...prev, field]
    );
  };



  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Container maxWidth="lg">
      <Slide direction="up" in={checked} mountOnEnter unmountOnExit>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Summary Filters
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
              <Grow in={checked} timeout={2500}>
              
                <DateTimePicker
                  label="Start Date and Time"
                  value={startDatetime}
                  onChange={(newValue) => setStartDatetime(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              
              </Grow>
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
              <Grow in={checked} timeout={2500}>
                <DateTimePicker
                  label="End Date and Time"
                  value={endDatetime}
                  onChange={(newValue) => setEndDatetime(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
                </Grow>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
             
              
              <Grow in={checked} timeout={2500}>
                <Select
                  fullWidth
                  value={selectedGroup}
                  onChange={handleGroupChange}
                  displayEmpty
                >
                  <MenuItem value="" disabled>
                    Select Machine Group
                  </MenuItem>
                  {machineGroups.map((group) => (
                    <MenuItem key={group.MachineGroupCode} value={group.MachineGroupCode}>
                      {group.GroupDescription}
                    </MenuItem>
                  ))}
                </Select>
                </Grow>
               
              </Grid>
              <Grid item xs={12} sm={6} md={1}>
              <Grow in={checked} timeout={2500}>
                <Button variant="contained" onClick={handleView} fullWidth>
                  View
                </Button>
                </Grow>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
        </Slide>

        <Box>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
          >
            {machines.map((machine) => (
              <Tab key={machine} label={machine} />
            ))}
          </Tabs>
          {machines.map((machine, index) => (
            <Box
              key={machine}
              role="tabpanel"
              hidden={tabValue !== index}
              sx={{ mt: 2 }}
            >
              {tabValue === index && (
                <>
                  <Card sx={{ mb: 3 }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Summary Data
                      </Typography>
                      <Button
                        onClick={handleColumnDialogToggle}
                        variant="outlined"
                        sx={{ mb: 2 }}
                      >
                        Manage Columns
                      </Button>
                      <DataGrid
                        rows={machineData[machine]?.map((row, idx) => ({
                          id: idx + 1,
                          ...row,
                        })) || []}
                        columns={summaryColumns}
                        pageSize={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        components={{
                          NoRowsOverlay: NoRowsOverlay
                        }}
                      />
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Planned Jobs Data
                      </Typography>
                      <DataGrid
                        rows={plannedJobsData[machine]?.map((row, idx) => ({
                          id: idx + 1,
                          ...row,
                        })) || []}
                        columns={[
                          { field: "machineCode", headerName: "Machine Code", width: 150 },
                          {
                            field: "planningFrom",
                            headerName: "Planning From",
                            width: 200,
                            renderCell: (params) => dayjs(params.value).format("YYYY-MM-DD HH:mm A"),
                          },
                          {
                            field: "planingTo",
                            headerName: "Planning To",
                            width: 200,
                            renderCell: (params) => dayjs(params.value).format("YYYY-MM-DD HH:mm A"),
                          },
                          { field: "jobSequenceNumber", headerName: "Job Seq. Number", width: 150 },
                          { field: "jobNumber", headerName: "Job Number", width: 200 },
                          { field: "WOdescription", headerName: "WO Description", width: 200 },
                          { field: "customer", headerName: "Customer", width: 200 },
                          { field: "WOpriority", headerName: "WO Priority", width: 150 },
                          { field: "WOQty", headerName: "WO Qty", width: 150 },
                          { field: "OPQty", headerName: "OP Qty", width: 150 },
                          { field: "plannedQty", headerName: "Planned Qty", width: 150 },
                          { field: "OpDesctiption", headerName: "Op Description", width: 200 },
                          { field: "ColorSequence", headerName: "Color Sequence", width: 200 },
                          { field: "OtherDetails", headerName: "Other Details", width: 250 },
                          { field: "SetupTime", headerName: "Setup Time", width: 150 },
                          { field: "OperationalTime", headerName: "Operational Time", width: 150 },
                          { field: "SetupAndOpeTime", headerName: "Setup + Oper. Time", width: 150 },
                          { field: "TimeUnit", headerName: "Time Unit", width: 150 },
                          { field: "opstartDate", headerName: "Op Start Date", width: 200 },
                          { field: "opendDate", headerName: "Op End Date", width: 200 },
                          { field: "WOduedate", headerName: "WO Due Date", width: 200 },
                          { field: "WOenddate", headerName: "WO End Date", width: 200 },
                          { field: "WOdelay", headerName: "WO Delay", width: 150 },
                        ]}
                        pageSize={10}
                        rowsPerPageOptions={[10, 20, 50]}
                        components={{
                          NoRowsOverlay: NoRowsOverlay
                        }}
                      />
                    </CardContent>
                  </Card>
                </>
              )}
            </Box>
          ))}
        </Box>

        <Dialog open={columnDialogOpen} onClose={handleColumnDialogToggle}>
          <DialogTitle>Manage Columns</DialogTitle>
          <DialogContent>
            {allColumns.map((col) => (
              <FormControlLabel
                key={col.field}
                control={
                  <Checkbox
                    checked={visibleColumns.includes(col.field)}
                    onChange={() => handleColumnChange(col.field)}
                  />
                }
                label={col.headerName}
              />
            ))}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleColumnDialogToggle}>Close</Button>
          </DialogActions>
        </Dialog>

        {snackbars.map((snackbar) => (
          <Snackbar
            key={snackbar.id}
            open
            autoHideDuration={5000}
            onClose={() => handleSnackbarClose(snackbar.id)}
            anchorOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MuiAlert
              onClose={() => handleSnackbarClose(snackbar.id)}
              severity={snackbar.type}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </MuiAlert>
          </Snackbar>
        ))}
      </Container>
    </LocalizationProvider>
  );
};

export default SummarySection;
